import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Trash2 } from 'lucide-react';

const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export function MerchantCouponList({
  filteredCoupons, statusFilter, setStatusFilter, sortBy, setSortBy,
  handleDelete, handleShareCoupon, generateCouponImage
}) {
  return (
    <>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="deleted">Excluidos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="sort-filter"><SelectValue placeholder="Ordenar por" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Mais Recente</SelectItem>
            <SelectItem value="most_used">Mais Ativados</SelectItem>
            <SelectItem value="least_used">Menos Ativados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Meus Cupons</h2>
        {filteredCoupons.length === 0 ? (
          <Card className="hero-card"><CardContent className="py-12 text-center"><p className="text-slate-200">Nenhum cupom encontrado</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredCoupons.map(coupon => (
              <Card key={coupon.coupon_id} data-testid={`coupon-${coupon.coupon_id}`} className="hero-card">
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-4">
                    {coupon.images && coupon.images.length > 0 && (
                      <div className="flex gap-2 shrink-0">
                        {coupon.images.slice(0, 3).map((img, idx) => (
                          <div key={idx} className="w-14 h-14 sm:w-16 sm:h-16 overflow-hidden rounded-md border border-white/20">
                            <img src={img} alt={`${coupon.title} - ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-base sm:text-xl font-bold text-white truncate">{coupon.title}</h3>
                        <Badge className="bg-white/20 text-white border-white/30 text-xs sm:text-sm">{coupon.discount_percentage}% OFF</Badge>
                        {coupon.is_deleted ? (
                          <Badge className="bg-red-500/80 text-white border-transparent text-xs">Excluido</Badge>
                        ) : !coupon.is_active ? (
                          <Badge className="bg-yellow-500/80 text-white border-transparent text-xs">Inativo</Badge>
                        ) : null}
                      </div>
                      <p className="text-sm text-slate-200 mb-2 truncate">{coupon.description}</p>
                      <div className="flex gap-2 sm:gap-4 text-xs text-slate-300 flex-wrap">
                        <span>{coupon.category}</span>
                        <span>{coupon.city}, {coupon.state}</span>
                        <span>Usos: {coupon.current_uses}/{coupon.max_uses || '\u221E'}</span>
                        <span>Exp: {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-start">
                      <Button size="sm" onClick={() => generateCouponImage(coupon)} className="bg-slate-600 hover:bg-slate-500 text-white" title="Baixar imagem do cupom" data-testid={`download-coupon-${coupon.coupon_id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" onClick={() => handleShareCoupon(coupon)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`share-coupon-${coupon.coupon_id}`}>
                        <WhatsAppIcon className="h-4 w-4" />
                      </Button>
                      {!coupon.is_deleted && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(coupon.coupon_id)} className="text-red-300 hover:text-red-200 hover:bg-red-900/30" data-testid={`delete-coupon-${coupon.coupon_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
